import { type FC } from "react";
import { Alert, Box, Card, CardContent, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  useEmailTemplateQuery,
  useMarketingLimits,
  type EmailTemplatePreviewProps,
} from "@/partner/entities/marketing";
import { useCurrentPlatformQuery } from "@/partner/entities/platforms";
import { toMarketingLanguage } from "../model/blocks";
import { fromEmailTemplate } from "../model/editorState";
import { EmailPreviewFrame } from "./EmailPreviewFrame";

/**
 * Превью выбранного письма на языке платформы (или на первом языке
 * письма), тот же рендер, что в редакторе писем.
 */
export const EmailTemplatePreviewById: FC<EmailTemplatePreviewProps> = ({
  emailTemplateId,
}) => {
  const { t } = useTranslation();
  const platform = useCurrentPlatformQuery();
  const limits = useMarketingLimits();
  const { data, loading } = useEmailTemplateQuery({
    variables: { id: String(emailTemplateId) },
    skip: !emailTemplateId,
  });

  const renderBody = () => {
    if (!emailTemplateId) {
      return (
        <Alert severity="info">
          {t("marketingErrors.BROADCAST_EMAIL_REQUIRED")}
        </Alert>
      );
    }
    if (!data) {
      return loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Alert severity="error">
          {t("marketingErrors.BROADCAST_EMAIL_NOT_FOUND")}
        </Alert>
      );
    }
    const { variants } = fromEmailTemplate(data.emailTemplate);
    const language = toMarketingLanguage(
      platform.data?.currentPlatform?.language?.code
    );
    const variant =
      variants.find((v) => v.language === language) ?? variants[0];
    if (!variant) return null;
    return (
      <EmailPreviewFrame
        key={`${emailTemplateId}-${variant.language}`}
        variant={variant}
        urlVariables={limits?.emailUrlVariables}
      />
    );
  };

  return (
    <Card elevation={0}>
      <CardContent>{renderBody()}</CardContent>
    </Card>
  );
};
